import { AnalyticIdentifier } from '@common/neo.tax';
import {
  ActionsMenuAction,
  ActionsMenuButton,
  Modal,
  Stack,
  Table,
  TableContainer,
  Typography,
} from '@ui-components/neo.tax';
import { useCallback, useMemo, useState } from 'react';

import { useTicketingConnections } from '../../hooks';
import { useAnalyticId } from '../../state';
import { TicketingConnectionAction, TicketingConnectionRow, toTicketingConnectionRows } from '../../utils';
import { DisconnectTicketingConnection } from './DisconnectTicketingConnection';
import { useTicketingConnectionColumns } from './hooks/useTicketingConnectionColumns';

/**
 * The company's ticketing connections, on the Data Sources page of both products.
 *
 * Company-level, not fiscal-year-level: a connection is a credential the whole company reads
 * through, so switching tax years must not change what is listed here. That is also why this sits
 * beside the fiscal-year data-sources table rather than in it — the two answer different questions
 * and their "disconnect" means different things.
 */
export const TicketingConnections = ({
  additionalActions = [],
  companyId,
  hasImportedData = false,
  onAddConnection,
  readOnly = false,
  slideOver,
}: {
  companyId: string;
  additionalActions?: ActionsMenuAction[];
  /** Imported files already provide the section's inventory, so an empty connection table adds no information. */
  hasImportedData?: boolean;
  /** Opens the connect slide-over scoped to ticketing providers. */
  onAddConnection: () => void;
  /**
   * Hides every write affordance — the "Add ticketing data" menu and the per-row actions — leaving
   * only the read-only list. For viewers whose connect/disconnect/upload the server rejects.
   */
  readOnly?: boolean;
  /**
   * How this page routes the row actions that continue into the connect slide-over — which the page
   * owns, not this component. `actions` is stated rather than inferred from the handler's presence,
   * so a page that wires up one of them does not end up rendering buttons for the others that do
   * nothing. Disconnect is always offered; this component performs it itself.
   */
  slideOver?: {
    actions: ReadonlySet<TicketingConnectionAction>;
    onAction: (action: TicketingConnectionAction, connection: TicketingConnectionRow) => void;
  };
}): React.ReactElement => {
  const [connectionToDisconnect, setConnectionToDisconnect] = useState<TicketingConnectionRow>();
  const addConnectionBtnId = useAnalyticId(AnalyticIdentifier.ADD_TICKETING_CONNECTION);
  const { company, loading } = useTicketingConnections(companyId);

  const rows = useMemo(
    () =>
      company
        ? toTicketingConnectionRows(company.ticketingTokens, {
            gitHubAppAvailable: company.gitHubAppAvailable,
          })
        : [],
    [company],
  );

  // Depends on the pieces rather than the `slideOver` object: pages build that inline, so it is a
  // new identity every render, and these two memos are what keep the column definitions from being
  // rebuilt on each of them.
  const slideOverActions = slideOver?.actions;
  const onSlideOverAction = slideOver?.onAction;

  const supportedActions = useMemo(
    () =>
      readOnly
        ? new Set<TicketingConnectionAction>()
        : new Set([TicketingConnectionAction.Disconnect, ...(slideOverActions ?? [])]),
    [readOnly, slideOverActions],
  );

  // Stable identity, because it is what the column definitions memoize on — a new function every
  // render would rebuild every column and re-derive the table instance on each parent render.
  const handleAction = useCallback(
    (action: TicketingConnectionAction, connection: TicketingConnectionRow): void => {
      if (action === TicketingConnectionAction.Disconnect) {
        setConnectionToDisconnect(connection);
        return;
      }
      onSlideOverAction?.(action, connection);
    },
    [onSlideOverAction],
  );

  const columns = useTicketingConnectionColumns({ onAction: handleAction, supportedActions });
  const showConnectionsTable = loading || rows.length > 0 || !hasImportedData;

  return (
    <Stack direction="column" gap={3} fullWidth className="shrink-0">
      {readOnly ? (
        <></>
      ) : (
        <Stack justifyContent="end" gap={3} wrap="wrap" fullWidth>
          <ActionsMenuButton
            label="Add ticketing data"
            collapseSingleAction
            actions={[
              { label: 'Connect a provider', onSelect: onAddConnection, id: addConnectionBtnId },
              ...additionalActions,
            ]}
          />
        </Stack>
      )}

      {showConnectionsTable ? (
        <TableContainer>
          <Table<TicketingConnectionRow>
            columns={columns}
            data={rows}
            autoHeight
            // Only once the first read has answered: this query resolves a live Merge probe per
            // Merge-managed connection, so an in-flight one would otherwise tell a company with
            // connections that it has none for as long as that takes.
            zeroState={loading ? undefined : <Typography fontSize="xs">No ticketing connections.</Typography>}
          />
        </TableContainer>
      ) : (
        <></>
      )}

      <Modal isOpen={!!connectionToDisconnect} setIsOpen={(): void => setConnectionToDisconnect(undefined)}>
        {connectionToDisconnect ? (
          <DisconnectTicketingConnection
            companyId={companyId}
            connection={connectionToDisconnect}
            close={(): void => setConnectionToDisconnect(undefined)}
          />
        ) : (
          <></>
        )}
      </Modal>
    </Stack>
  );
};
